import React from "react";

export default function HowBonus({
  depositBonusPer = 30,
  lotsPer100 = 2.5,
}) {
  const formatLots = (num) => {
    return (
      Number(num)
        .toFixed(2)
        .replace(/\.00$/, "")
        .replace(/(\.[0-9])0$/, "$1") + "L"
    );
  };

  const calculationExamples = [
    {
      deposit: "$100",
      bonus: `$${Math.round(100 * (Number(depositBonusPer) / 100))}`,
      calculation: `Base Unit (${lotsPer100}L per $100)`,
      lotsRequired: formatLots(1 * Number(lotsPer100)),
    },
    {
      deposit: "$700",
      bonus: `$${Math.round(700 * (Number(depositBonusPer) / 100))}`,
      calculation: `7 × ${lotsPer100}L`,
      lotsRequired: formatLots(7 * Number(lotsPer100)),
    },
    {
      deposit: "$1,300",
      bonus: `$${Math.round(1300 * (Number(depositBonusPer) / 100))}`,
      calculation: `13 × ${lotsPer100}L`,
      lotsRequired: formatLots(13 * Number(lotsPer100)),
    },
    {
      deposit: "$1,330",
      bonus: `$${Math.round(1330 * (Number(depositBonusPer) / 100))}`,
      calculation: `14 × ${lotsPer100}L (rounded up)`,
      lotsRequired: formatLots(14 * Number(lotsPer100)),
    },
    {
      deposit: "$5,000",
      bonus: `$${Math.round(5000 * (Number(depositBonusPer) / 100))}`,
      calculation: `50 × ${lotsPer100}L`,
      lotsRequired: formatLots(50 * Number(lotsPer100)),
    },
  ];

  return (
    <div
      className="card border h-100 shadow-sm"
      style={{
        borderRadius: "var(--bs-border-radius-xl)",
        backgroundColor: "var(--bs-card-bg)",
        borderColor: "var(--bs-border-color)",
      }}
    >
      <div className="card-body p-4 d-flex flex-column justify-content-between">
        <div>
          {/* Header */}
          <div className="mb-3">
            <h5
              className="fw-bold mb-1"
              style={{ color: "var(--bs-emphasis-color)", fontSize: "1.1rem" }}
            >
              How Bonus &amp; Lots Are Calculated
            </h5>
            <p className="mb-0 font-size-13" style={{ color: "var(--bs-secondary-color)" }}>
              Examples based on the{" "}
              <strong style={{ color: "var(--bs-primary)", paddingLeft: "8px" }}>
                $100 = {lotsPer100}L requirement
              </strong>
            </p>
          </div>

          {/* Clean Modern Calculation Table */}
          <div className="table-responsive">
            <table className="table table-borderless align-middle mb-0">
              <thead>
                <tr
                  style={{
                    borderBottom: "1px solid var(--bs-border-color)",
                    backgroundColor: "var(--bs-tertiary-bg)",
                  }}
                >
                  <th
                    className="text-uppercase py-2 ps-3 font-size-11 fw-semibold"
                    style={{
                      color: "var(--bs-secondary-color)",
                      letterSpacing: "0.5px",
                    }}
                  >
                    DEPOSIT
                  </th>
                  <th
                    className="text-uppercase py-2 font-size-11 fw-semibold text-center"
                    style={{
                      color: "var(--bs-secondary-color)",
                      letterSpacing: "0.5px",
                    }}
                  >
                    BONUS
                  </th>
                  <th
                    className="text-uppercase py-2 font-size-11 fw-semibold text-center"
                    style={{
                      color: "var(--bs-secondary-color)",
                      letterSpacing: "0.5px",
                    }}
                  >
                    CALCULATION
                  </th>
                  <th
                    className="text-uppercase py-2 pe-3 font-size-11 fw-semibold text-end"
                    style={{
                      color: "var(--bs-secondary-color)",
                      letterSpacing: "0.5px",
                    }}
                  >
                    LOTS REQUIRED
                  </th>
                </tr>
              </thead>
              <tbody>
                {calculationExamples.map((item, idx) => (
                  <tr
                    key={idx}
                    style={{
                      borderBottom:
                        idx === calculationExamples.length - 1
                          ? "none"
                          : "1px solid var(--bs-border-color)",
                    }}
                    className="hover-bg-light"
                  >
                    <td
                      className="py-2 ps-3 fw-bold"
                      style={{ color: "var(--bs-emphasis-color)", fontSize: "13px" }}
                    >
                      {item.deposit}
                    </td>
                    <td
                      className="py-2 text-center fw-bold"
                      style={{ color: "var(--bs-primary)", fontSize: "13px" }}
                    >
                      {item.bonus}
                    </td>
                    <td
                      className="py-2 text-center"
                      style={{ color: "var(--bs-secondary-color)", fontSize: "12px" }}
                    >
                      {item.calculation}
                    </td>
                    <td
                      className="py-2 pe-3 text-end fw-bold"
                      style={{ color: "var(--bs-body-color)", fontSize: "13px" }}
                    >
                      {item.lotsRequired}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footnote matching design */}
        <div
          className="pt-3 border-top mt-3"
          style={{ borderColor: "var(--bs-border-color)" }}
        >
          <p
            className="mb-0 font-size-11 fst-italic"
            style={{ color: "var(--bs-secondary-color)" }}
          >
            * Odd deposit amounts (e.g., $1,330) round up to the nearest $100 unit for lot calculation.
          </p>
        </div>
      </div>
    </div>
  );
}
